import {
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import UploadIcon from '@mui/icons-material/Publish';
import DownloadIcon from '@mui/icons-material/GetApp';
import { useContext, useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import UploadInvoiceDialog from "../components/UploadInvoiceDialog";
import moment from "moment";
import { forEach } from "lodash";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const PAGE_SIZE = 30;

const columns = [
  {
    field: "ats_invoice",
    headerName: "ATS Invoice",
    width: 120,
    sortable: false,
  },
  {
    field: "shopify_order_number",
    headerName: "Order #",
    width: 120,
    sortable: false,
  },
  {
    field: "shopify_created_at",
    headerName: "Date",
    width: 150,
    sortable: false,
    valueGetter: (value) =>
      value
        ? format(new Date(value), "yyyy-MM-dd")
        : "",
  },
  { field: "shopify_city", headerName: "City", width: 150, sortable: false },
  {
    field: "shopify_province",
    headerName: "Province",
    width: 150,
    sortable: false,
  },
  {
    field: "shopify_order_total",
    headerName: "Order",
    width: 120,
    sortable: false,
    valueGetter: (value) => value ? `$${value}` : "",
  },
  {
    field: "ats_total_pcs",
    headerName: "PCS",
    type: "number",
    width: 80,
    sortable: false,
  },
  {
    field: "ats_total",
    headerName: "ATS",
    width: 100,
    type: "number",
    sortable: false,
    valueGetter: (value) => value ? `$${value}` : "",
  },
  {
    field: "shopify_total",
    headerName: "Shipping",
    width: 100,
    type: "number",
    sortable: false,
    valueGetter: (value) => value ? `$${value}` : ""
  },
  {
    field: "shipping_diff",
    headerName: "Diff",
    width: 100,
    type: "number",
    sortable: true,
    valueGetter: (value) => value ? `$${Number(value).toFixed(2)}` : "",
  },
];

export default function ATSInvoices() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [filter, setFilter] = useState({
    invoiceNumber: "",
    dateFrom: null,
    dateTo: null,
    province: "",
    city: ""
  });
  const [orders, setOrders] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);

  const showDialog = () => setDialogVisible(true);
  const hideDialog = (reload) => {
    setDialogVisible(false);
    if (reload) {
      loadOrders(true);
    }
  }

  const handleResetFilters = () => {
    setFilter({
      invoiceNumber: "",
      dateFrom: null,
      dateTo: null,
      province: "",
      city: ""
    });
  };

  let active = useRef(false);

  const getOrderBy = () => {
    let orderBy = "";
    forEach(sortModel, (model) => {
      orderBy += `${orderBy ? ", " : ""}${model.field} ${model.sort}`
    });
    return orderBy;
  };

  const loadOrders = async (reset) => {
    setIsLoading(true);
    const params = new URLSearchParams({
      id: filter.invoiceNumber,
      dateFrom: filter.dateFrom ? moment(filter.dateFrom).startOf('day').format() : "",
      dateTo: filter.dateTo ? moment(filter.dateTo).endOf('day').format() : "",
      province: filter.province,
      city: filter.city,
      limit: PAGE_SIZE,
      offset: reset ? 0 : paginationModel.page * PAGE_SIZE,
      orderBy: getOrderBy()
    });
    const response = await fetchWithInterceptor(
      `/api/ats/invoice-orders/?${params}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      }
    );
    if (response.ok) {
      if (!active.current) {
        return;
      }

      const data = await response.json();
      if (reset) {
        setPaginationModel({ ...paginationModel, page: 0 });
      }
      setOrders(data.orders);
      setRowCount(data.total);
    } else {
      console.log(response);
      appContext.showMessage(
        "There was an error getting the invoice info",
        "error"
      );
    }
    setIsLoading(false);
  };

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    loadOrders(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadOrders(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sortModel]);

  const handleExport = async () => {
    try {
      setIsDownloading(true);
      const params = new URLSearchParams({
        id: filter.invoiceNumber,
        dateFrom: filter.dateFrom ? moment(filter.dateFrom).format() : "",
        dateTo: filter.dateTo ? moment(filter.dateTo).format() : "",
        province: filter.province,
        city: filter.city
      });
      const response = await fetchWithInterceptor(
        `/api/ats/invoice-orders-export/?${params}`,
        {
          method: "GET",
          headers: {
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (response.ok) {
        const blob = await response.blob();
        var a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.setAttribute("download", `${Date.now()}_file.csv`);
        a.click();
      }
      setIsDownloading(false);
    } catch (error) {
      console.log(error);
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Upload ATS invoices to compare shipping rates charged to customers against actual costs billed by the courier. Use this tool to identify discrepancies and ensure shipping profitability.
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  label="ATS Invoice"
                  variant="outlined"
                  value={filter.invoiceNumber}
                  onChange={(e) =>
                    setFilter({ ...filter, invoiceNumber: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <DatePicker
                  label="Date from"
                  value={filter.dateFrom}
                  onChange={(date) => setFilter({ ...filter, dateFrom: date })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <DatePicker
                  label="Date to"
                  value={filter.dateTo}
                  onChange={(date) => setFilter({ ...filter, dateTo: date })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  label="City"
                  variant="outlined"
                  value={filter.city}
                  onChange={(e) =>
                    setFilter({ ...filter, city: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  label="Province"
                  variant="outlined"
                  value={filter.province}
                  onChange={(e) =>
                    setFilter({ ...filter, province: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleResetFilters}>
                  RESET FILTERS
                </Button>
              </Grid>
            </Grid>
          </div>
          <Divider />

          <Grid container justifyContent="space-between" style={{ padding: 16 }}>
            <Grid>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={handleExport}
                startIcon={<DownloadIcon />}
                disabled={isDownloading}
              >
                Export
              </Button>
            </Grid>
            <Grid>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={showDialog}
                startIcon={<UploadIcon />}
              >
                Upload Invoice
              </Button>
            </Grid>
          </Grid>

          <DataGrid
            rows={orders}
            columns={columns}
            pageSize={PAGE_SIZE}
            pageSizeOptions={[PAGE_SIZE]}
            disableSelectionOnClick
            disableColumnFilter
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={(model) => setSortModel(model)}
            rowCount={rowCount}
            loading={isLoading}
            getRowClassName={(params) => {
              const diff = params?.row?.shipping_diff ?? 0;
              if (diff < 0) {
                return "row-negative";
              }
              return null;
            }}
            sx={{
              height: 52 * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": {
                display: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
              },
              "& .row-negative": {
                color: "red",
              },
            }}
          />
        </Paper>
        <UploadInvoiceDialog open={dialogVisible} onClose={hideDialog} />
      </Container>
    </>
  );
}
